/**
 * Role-based access control helper
 *
 * This utility provides functions to check user permissions based on their roles.
 * Roles are stored in the user object as an array.
 */

import { Role, normalizeRole } from '../constants/roles';

export interface User {
  id?: string;
  email?: string;
  roles?: string[];
  canAccessUserManagement?: boolean;
}

/**
 * Check if a user has a specific role
 */
export const hasRole = (user: User | null | undefined, role: Role): boolean => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  const normalizedUserRoles = user.roles.map((r) => normalizeRole(r));
  return normalizedUserRoles.includes(role);
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (user: User | null | undefined, roles: Role[]): boolean => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  const normalizedUserRoles = user.roles.map((r) => normalizeRole(r));
  return roles.some((role) => normalizedUserRoles.includes(role));
};

/**
 * Check if a user is a super admin
 */
export const isSuperAdmin = (user: User | null | undefined): boolean => {
  return hasRole(user, Role.superAdmin);
};

/**
 * Check if a user is an admin (including super admin)
 */
export const isAdmin = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, [Role.superAdmin, Role.admin]);
};

/**
 * Check if a user is a dock coordinator (includes manager, admin, and super admin)
 */
export const isDockCoordinator = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, [
    Role.dockCoordinator,
    Role.manager,
    Role.admin,
    Role.superAdmin
  ]);
};

/**
 * Check if a user is a carrier
 */
export const isCarrier = (user: User | null | undefined): boolean => {
  return hasRole(user, Role.carrier);
};

/**
 * Check if a user is a spotter
 */
export const isSpotter = (user: User | null | undefined): boolean => {
  return hasRole(user, Role.spotter);
};

/**
 * Check if a user is a third-party yard spotter ("Third-Party Yard Spotter")
 */
export const isThirdPartyYardSpotter = (user: User | null | undefined): boolean => {
  return hasRole(user, Role.thirdPartyYardSpotter);
};

/**
 * Check if a user is any kind of spotter (spotter or third-party yard spotter).
 * Spotters get the mobile/android layout.
 */
export const isAnySpotter = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, [Role.spotter, Role.thirdPartyYardSpotter]);
};

/**
 * Check if a user is only a third-party yard spotter. Like the backend RBAC,
 * any other role alongside it grants that role's access.
 */
const isOnlyThirdPartyYardSpotter = (user: User | null | undefined): boolean => {
  if (!isThirdPartyYardSpotter(user)) {
    return false;
  }
  return user!.roles!.every((r) => normalizeRole(r) === Role.thirdPartyYardSpotter);
};

/**
 * Check if a user can cancel tasks (third-party yard spotters cannot)
 */
export const canCancelTasks = (user: User | null | undefined): boolean => {
  return !isOnlyThirdPartyYardSpotter(user);
};

/**
 * Check if a user can unassign a task assigned to someone else
 * (third-party yard spotters can only unassign themselves)
 */
export const canUnassignAnyTask = (user: User | null | undefined): boolean => {
  return !isOnlyThirdPartyYardSpotter(user);
};

/**
 * Which movement tasks a user may edit the source and destination of, as the backend
 * decides it (Permissions.movement_task_edit_scope): 'any' for dock coordinators and up,
 * 'own' (tasks assigned to them) for spotters and third-party yard spotters, else 'none'.
 */
export type MovementTaskEditScope = 'any' | 'own' | 'none';

export const movementTaskEditScope = (user: User | null | undefined): MovementTaskEditScope => {
  if (isDockCoordinator(user)) return 'any';
  if (isAnySpotter(user)) return 'own';
  return 'none';
};

/**
 * Check if a user is a transport user
 */
export const isTransport = (user: User | null | undefined): boolean => {
  return hasRole(user, Role.transport);
};

/**
 * Check if a user has read-only shipments access (carrier or transport)
 */
export const isReadOnlyShipments = (user: User | null | undefined): boolean => {
  return isCarrier(user) || isTransport(user);
};

/**
 * Check if a user can access task app (spotter or dock coordinator+)
 */
export const canAccessTaskApp = (user: User | null | undefined): boolean => {
  return hasAnyRole(user, [
    Role.spotter,
    Role.thirdPartyYardSpotter,
    Role.dockCoordinator,
    Role.manager,
    Role.admin,
    Role.superAdmin
  ]);
};

/**
 * Check if a user is a FreightRoll staff member, i.e. their email is on the
 * @freightroll.com domain. Used to gate internal-only UI (debug views,
 * unreleased features) rather than customer-facing role/permission checks.
 */
export const isFreightrollUser = (user: User | null | undefined): boolean => {
  return !!user?.email && user.email.split('@')[1] === 'freightroll.com';
};

export interface NavPermissions {
  carrierTrailers: boolean;
  transportTrailers: boolean;
  dashboard: boolean;
  shipments: boolean;
  driverMgt: boolean;
  driverComms: boolean;
  dockMgt: boolean;
  yardSpots: boolean;
  yardChecks: boolean;
  trailerHistory: boolean;
  whiteboard: boolean;
  taskHistory: boolean;
  taskApp: boolean;
  trailers: boolean;
  facilities: boolean;
}

/**
 * Compute which nav items a user can see based on their role and feature flags.
 * Single source of truth for sidebar visibility — no scattered boolean logic.
 */
export const getNavPermissions = (
  user: User | null | undefined,
  opts: { ymsEnabled: boolean }
): NavPermissions => {
  const carrier = isCarrier(user);
  const transport = isTransport(user);
  const dockCoord = isDockCoordinator(user);
  const { ymsEnabled } = opts;

  // Restricted roles only see their own section
  if (carrier) {
    return {
      carrierTrailers: true,
      transportTrailers: false,
      dashboard: false,
      shipments: true,
      driverMgt: false,
      driverComms: false,
      dockMgt: false,
      yardSpots: false,
      yardChecks: false,
      trailerHistory: false,
      whiteboard: false,
      taskHistory: false,
      taskApp: false,
      trailers: false,
      facilities: false
    };
  }

  if (transport) {
    return {
      carrierTrailers: false,
      transportTrailers: true,
      dashboard: false,
      shipments: true,
      driverMgt: false,
      driverComms: false,
      dockMgt: false,
      yardSpots: false,
      yardChecks: false,
      trailerHistory: false,
      whiteboard: false,
      taskHistory: false,
      taskApp: false,
      trailers: false,
      facilities: false
    };
  }

  // Standard roles
  const yms = dockCoord && ymsEnabled;
  return {
    carrierTrailers: false,
    transportTrailers: false,
    dashboard: ymsEnabled,
    shipments: dockCoord,
    driverMgt: yms,
    driverComms: yms,
    dockMgt: yms,
    yardSpots: yms,
    yardChecks: yms,
    trailerHistory: yms,
    whiteboard: yms,
    taskHistory: yms,
    taskApp: yms,
    trailers: yms,
    facilities: true
  };
};
